/**
 * ClientHistoryTab — extracted from Clients.jsx in iter170
 * (component split step #2). Renders the "History" tab inside the
 * customer detail Sheet: completed work orders, cancelled WOs, won
 * proposals, lost proposals, and archived equipment, each with their
 * own restore/copy/view actions.
 *
 * Stateless render component — owns no useState. All data and
 * handlers are passed in from the Clients.jsx parent so this file
 * can be split out cleanly without lifting any other state.
 */
import { useState } from "react";
import SearchBar from "@/components/common/SearchBar";
import FoundationDispatchHistory from "@/components/integrations/FoundationDispatchHistory";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { History, Plus, RotateCcw, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { updateWorkOrder, updateProposal } from "@/lib/api";
import { formatCurrency } from "@/pages/clients/clientsUtils";
import { shortDate, money } from "@/lib/format";

export function ClientHistoryTab({
  // Lists rendered by the tab
  completedWorkOrders: completedWorkOrdersIn,
  cancelledWorkOrders: cancelledWorkOrdersIn,
  wonProposals: wonProposalsIn,
  lostProposals: lostProposalsIn,
  filteredArchivedEquipment: filteredArchivedEquipmentIn,
  // iter241 — unified communications timeline + archived notes
  communications: communicationsIn = [],
  archivedNotes: archivedNotesIn = [],
  onRestoreNote,
  documentsSection = null,
  // Context required by row handlers
  selectedClient,
  selectedSiteId,
  // Lookups
  getSiteNameById,
  getEquipmentTypeName,
  // Parent-controlled actions
  setPendingViewWO,
  setPendingCopyWO,
  setActiveDetailTab,
  loadClientDetails,
  handleRestoreEquipment,
}) {
  const navigate = useNavigate();
  const [showAllComms, setShowAllComms] = useState(false);
  // One search box filters every list on the tab.
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const hit = (...vals) => !q || vals.some((v) => (v == null ? "" : String(v)).toLowerCase().includes(q));
  const completedWorkOrders = completedWorkOrdersIn.filter((wo) => hit(wo.wo_number, wo.title, getSiteNameById(wo.site_id)));
  const cancelledWorkOrders = cancelledWorkOrdersIn.filter((wo) => hit(wo.wo_number, wo.title, getSiteNameById(wo.site_id)));
  const wonProposals = wonProposalsIn.filter((p) => hit(p.proposal_number, p.title, p.name));
  const lostProposals = lostProposalsIn.filter((p) => hit(p.proposal_number, p.title, p.name));
  const filteredArchivedEquipment = filteredArchivedEquipmentIn.filter((e) => hit(e.name, e.location, getSiteNameById(e.site_id)));
  const communications = communicationsIn.filter((c) => hit(c.subject, c.to, c.doc_type, c.status, c.recipient));
  const archivedNotes = archivedNotesIn; // restore works by index — never filtered

  const SOURCE_COLORS = {
    "Proposal": "bg-orange-100 text-orange-700",
    "Work Order": "bg-blue-100 text-blue-700",
    "Change Order": "bg-indigo-100 text-indigo-700",
    "Invoicing": "bg-emerald-100 text-emerald-700",
    "Purchasing": "bg-amber-100 text-amber-700",
    "Manual Log": "bg-slate-100 text-slate-600",
  };
  const statusChip = (s) => {
    if (s === "sent") return "bg-emerald-100 text-emerald-700";
    if (s === "logged") return "bg-slate-100 text-slate-500";
    if (String(s).startsWith("skipped")) return "bg-amber-100 text-amber-700";
    return "bg-rose-100 text-rose-700";
  };
  const fmtCommDate = (d) => {
    try { return format(new Date(d), "MMM d, yyyy h:mm a"); } catch { return String(d).slice(0, 10); }
  };
  const visibleComms = showAllComms ? communications : communications.slice(0, 5);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 flex-wrap">
        <SearchBar value={search} onChange={setSearch} placeholder="Search history..." testId="search-history" />
        <span className="ml-auto text-xs text-slate-500 num">{completedWorkOrders.length + cancelledWorkOrders.length} work orders · {wonProposals.length + lostProposals.length} proposals</span>
      </div>
      {/* Communications — every email/log tied to this customer (iter241) */}
      <div data-testid="history-communications">
        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">Communications
          {communications.length > 0 && (
            <span className="text-xs font-medium bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{communications.length}</span>
          )}
        </h4>
        {communications.length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-sm border rounded-lg">No communications yet</div>
        ) : (
          <div className="border rounded-lg divide-y divide-slate-100">
            {visibleComms.map((c, i) => (
              // eslint-disable-next-line react/no-array-index-key -- read-only timeline
              <div key={`comm-${i}`} className="flex items-center gap-3 px-3 py-2" data-testid={`history-comm-${i}`}>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${SOURCE_COLORS[c.source] || "bg-slate-100 text-slate-600"}`}>{c.source}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-slate-700 truncate" title={c.subject}>{c.subject || "(no subject)"}</div>
                  <div className="text-[10px] text-slate-400 truncate">
                    {fmtCommDate(c.date)}{c.to ? ` · to ${c.to}` : ""}{c.ref_label ? ` · ${c.ref_label}` : ""}
                  </div>
                </div>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0 ${statusChip(c.status)}`}>
                  {String(c.status).replace(/_/g, " ")}
                </span>
              </div>
            ))}
            {communications.length > 5 && (
              <button
                type="button"
                className="w-full text-center py-2 text-[11px] text-brand font-medium hover:bg-slate-50"
                onClick={() => setShowAllComms(s => !s)}
                data-testid="history-comms-show-all"
              >
                {showAllComms ? "Show less" : `Show all ${communications.length} communications`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Archived Notes (iter241) */}
      {archivedNotes.length > 0 && (
        <div data-testid="history-archived-notes">
          <h4 className="text-sm font-semibold text-slate-700 mb-3">Archived Notes</h4>
          <div className="border rounded-lg divide-y divide-slate-100">
            {archivedNotes.map((n, i) => (
              // eslint-disable-next-line react/no-array-index-key -- archived log, newest-first
              <div key={`anote-${i}`} className="flex items-start gap-3 px-3 py-2" data-testid={`history-archived-note-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] text-slate-400 font-medium">
                    {shortDate(n.date)}
                    {n.archived_at ? ` · archived ${shortDate(n.archived_at)}` : ""}
                  </div>
                  <div className="text-sm text-slate-700">{n.text}</div>
                </div>
                {onRestoreNote && (
                  <button
                    type="button"
                    title="Restore to Notes"
                    className="shrink-0 p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                    onClick={() => onRestoreNote(i)}
                    data-testid={`btn-restore-note-${i}`}
                  >
                    <ArchiveRestore className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completed Work Orders */}
      <div>
        <h4 className="text-sm font-semibold text-slate-700 mb-3">Completed Work Orders</h4>
        {completedWorkOrders.length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-sm border rounded-lg">No completed work orders</div>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="th-std">WO #</TableHead>
                  <TableHead className="th-std">Title</TableHead>
                  <TableHead className="th-std">Site</TableHead>
                  <TableHead className="th-std">Total</TableHead>
                  <TableHead className="th-std">Status</TableHead>
                  <TableHead className="th-std">Completed</TableHead>
                  <TableHead className="th-std text-center">Annual</TableHead>
                  <TableHead className="th-std w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {completedWorkOrders.map(wo => (
                  <TableRow key={wo.id} className="cursor-pointer table-row-hover" data-testid={`history-wo-${wo.id}`}
                    onClick={() => { setPendingViewWO(wo); setActiveDetailTab("workorders"); }}>
                    <TableCell className="text-xs font-mono font-medium text-brand">{wo.wo_number || "-"}</TableCell>
                    <TableCell className="font-medium text-xs">{wo.title}</TableCell>
                    <TableCell className="text-xs text-slate-600">{getSiteNameById(wo.site_id) || "-"}</TableCell>
                    <TableCell className="text-xs font-medium num">{money(wo.total)}</TableCell>
                    <TableCell className="text-xs"><span className="inline-flex w-[88px] justify-center text-[10px] px-2 py-0.5 rounded-md font-semibold bg-green-100 text-green-700">Completed</span></TableCell>
                    <TableCell className="text-xs text-slate-500 tabular-nums">{shortDate(wo.updated_at || wo.work_date)}</TableCell>
                    <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={wo.annual_service || false}
                        className="h-4 w-4 rounded border-slate-300 accent-blue-600 cursor-pointer"
                        onChange={async (e) => {
                          try {
                            await updateWorkOrder(wo.id, { ...wo, annual_service: e.target.checked });
                            toast.success(e.target.checked ? "Marked as annual service" : "Annual service removed");
                            loadClientDetails(selectedClient);
                          } catch { toast.error("Failed to update"); }
                        }}
                        data-testid={`annual-toggle-${wo.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-xs">
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button variant="outline" size="sm" className="h-7 text-xs"
                          onClick={() => { setPendingViewWO(wo); setActiveDetailTab("workorders"); }}
                          data-testid={`view-completed-wo-${wo.id}`}>
                          View
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs text-amber-600 border-amber-200 hover:bg-amber-50"
                          onClick={async () => {
                            try {
                              await updateWorkOrder(wo.id, { ...wo, status: "draft" });
                              toast.success("Work order restored to draft");
                              loadClientDetails(selectedClient);
                            } catch { toast.error("Failed to restore"); }
                          }}
                          data-testid={`restore-completed-wo-${wo.id}`}>
                          Restore
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs"
                          onClick={() => {
                            setPendingCopyWO(wo);
                            setActiveDetailTab("workorders");
                          }}
                          data-testid={`copy-wo-${wo.id}`}>
                          <Plus className="w-3 h-3 mr-1" /> Copy
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Cancelled Work Orders */}
      <div>
        <h4 className="text-sm font-semibold text-slate-700 mb-3">Cancelled Work Orders</h4>
        {cancelledWorkOrders.length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-sm border rounded-lg">No cancelled work orders</div>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="th-std">WO #</TableHead>
                  <TableHead className="th-std">Title</TableHead>
                  <TableHead className="th-std">Site</TableHead>
                  <TableHead className="th-std">Total</TableHead>
                  <TableHead className="th-std">Status</TableHead>
                  <TableHead className="th-std">Date</TableHead>
                  <TableHead className="th-std w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cancelledWorkOrders.map(wo => (
                  <TableRow key={wo.id} className="table-row-hover" data-testid={`history-cancelled-wo-${wo.id}`}>
                    <TableCell className="text-xs font-mono font-medium">{wo.wo_number || "-"}</TableCell>
                    <TableCell className="font-medium text-xs">{wo.title}</TableCell>
                    <TableCell className="text-xs text-slate-600">{getSiteNameById(wo.site_id) || "-"}</TableCell>
                    <TableCell className="text-xs font-medium num">{money(wo.total)}</TableCell>
                    <TableCell className="text-xs"><span className="inline-flex w-[88px] justify-center text-[10px] px-2 py-0.5 rounded-md font-semibold bg-red-100 text-red-700">Cancelled</span></TableCell>
                    <TableCell className="text-xs text-slate-500 tabular-nums">{shortDate(wo.updated_at || wo.work_date)}</TableCell>
                    <TableCell className="text-xs">
                      <Button variant="outline" size="sm" className="h-7 text-xs"
                        onClick={async () => {
                          try {
                            await updateWorkOrder(wo.id, { ...wo, status: "draft" });
                            toast.success("Work order restored to draft");
                            loadClientDetails(selectedClient);
                          } catch { toast.error("Failed to restore"); }
                        }}
                        data-testid={`restore-wo-${wo.id}`}>
                        Restore
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Won Proposals */}
      <div>
        <h4 className="text-sm font-semibold text-slate-700 mb-3">Won Proposals</h4>
        {wonProposals.length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-sm border rounded-lg">No won proposals</div>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="th-std">Proposal</TableHead>
                  <TableHead className="th-std">Site</TableHead>
                  <TableHead className="th-std">Value</TableHead>
                  <TableHead className="th-std">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {wonProposals.map(p => (
                  <TableRow key={p.id} className="cursor-pointer table-row-hover" data-testid={`history-won-proposal-${p.id}`}
                    onClick={() => navigate(`/proposals?edit_id=${p.id}`)}>
                    <TableCell className="font-medium text-xs text-brand hover:underline">{p.name}</TableCell>
                    <TableCell className="text-xs text-slate-600">{getSiteNameById(p.site_id) || "-"}</TableCell>
                    <TableCell className="text-xs font-medium tabular-nums">{formatCurrency(p.value || p.amount || 0)}</TableCell>
                    <TableCell className="text-xs"><span className="inline-flex w-[88px] justify-center text-[10px] px-2 py-0.5 rounded-md font-semibold bg-green-100 text-green-700">Won</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Lost Proposals */}
      <div>
        <h4 className="text-sm font-semibold text-slate-700 mb-3">Lost Proposals</h4>
        {lostProposals.length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-sm border rounded-lg">No lost proposals</div>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="th-std">Proposal</TableHead>
                  <TableHead className="th-std">Site</TableHead>
                  <TableHead className="th-std">Value</TableHead>
                  <TableHead className="th-std">Status</TableHead>
                  <TableHead className="th-std w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lostProposals.map(p => (
                  <TableRow key={p.id} className="cursor-pointer table-row-hover" data-testid={`history-lost-proposal-${p.id}`}
                    onClick={() => navigate(`/proposals?edit_id=${p.id}`)}>
                    <TableCell className="font-medium text-xs text-brand hover:underline">{p.name}</TableCell>
                    <TableCell className="text-xs text-slate-600">{getSiteNameById(p.site_id) || "-"}</TableCell>
                    <TableCell className="text-xs font-medium tabular-nums">{formatCurrency(p.value || p.amount || 0)}</TableCell>
                    <TableCell className="text-xs"><span className="inline-flex w-[88px] justify-center text-[10px] px-2 py-0.5 rounded-md font-semibold bg-red-100 text-red-700">Lost</span></TableCell>
                    <TableCell className="text-xs">
                      <Button variant="outline" size="sm" className="h-7 text-xs"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await updateProposal(p.id, { ...p, status: "draft" });
                            toast.success("Proposal restored to draft");
                            loadClientDetails(selectedClient);
                          } catch { toast.error("Failed to restore"); }
                        }}
                        data-testid={`restore-proposal-${p.id}`}>
                        Restore
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Archived Equipment */}
      <div>
        <h4 className="text-sm font-semibold text-slate-700 mb-3">Archived Equipment</h4>
        {filteredArchivedEquipment.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <History className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p>No history {selectedSiteId !== "all" ? "for this site" : "yet"}</p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="th-std">Equipment</TableHead>
                  <TableHead className="th-std">Service Type</TableHead>
                  <TableHead className="th-std">Description</TableHead>
                  <TableHead className="th-std">Site</TableHead>
                  <TableHead className="th-std">Last Serviced</TableHead>
                  <TableHead className="th-std">Notes</TableHead>
                  <TableHead className="th-std w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredArchivedEquipment.map(equip => (
                  <TableRow key={equip.id} className="table-row-hover" data-testid={`archived-row-${equip.id}`}>
                    <TableCell className="font-medium text-xs">{equip.name}</TableCell>
                    <TableCell className="text-xs text-slate-600">{equip.service_type || "-"}</TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {equip.equipment_type_id ? getEquipmentTypeName(equip.equipment_type_id) : "-"}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{getSiteNameById(equip.site_id) || "-"}</TableCell>
                    <TableCell className="text-xs">
                      {equip.last_serviced ? (
                        <span className="text-emerald-600 num">{shortDate(equip.last_serviced)}</span>
                      ) : "-"}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[200px] truncate">
                      {Array.isArray(equip.notes) && equip.notes.length > 0 ? equip.notes[0].text : "-"}
                    </TableCell>
                    <TableCell className="text-xs">
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-brand hover:text-brand"
                        onClick={(e) => handleRestoreEquipment(e, equip.id)}
                        data-testid={`restore-equipment-${equip.id}`}>
                        <RotateCcw className="w-3 h-3 mr-1" /> Restore
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      {/* Foundation dispatch history (moved from its own tab) — read-only mirror of the bridge cache. */}
      {/* Documents (moved from its own tab) — only PDFs without another History list: executed WOs, change orders, POs, inspection reports. */}
      {documentsSection && (
        <div data-testid="history-documents" className="space-y-3">
          <h4 className="text-sm font-semibold text-slate-700">Documents</h4>
          {documentsSection}
        </div>
      )}
      <div data-testid="history-foundation">
        <FoundationDispatchHistory clientId={selectedClient?.id} />
      </div>
    </div>
  );
}
